import { PermissionCode } from '@shared/common/enums';

export interface ServiceCategory {
    key: string;
    label: string;
    icon: string;
    types: string[];
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
    { key: 'Flight', label: 'Flight', icon: 'flight', types: ['Flight', 'FlightGroup', 'FlightAssistance', 'FlightGroupAssistance'] },
    { key: 'Visa', label: 'Visa', icon: 'credit_card', types: ['Visa', 'VisaAssistance'] },
    { key: 'Passport', label: 'Passport', icon: 'auto_stories', types: ['Passport', 'PassportAssistance'] },
    { key: 'Insurance', label: 'Travel Insurance', icon: 'shield', types: ['TravelInsurance', 'TravelInsuranceAssistance'] },
    { key: 'Permit', label: 'Driving Permit', icon: 'description', types: ['DrivingPermit', 'DrivingPermitAssistance'] },
    { key: 'Hotel', label: 'Hotel', icon: 'hotel', types: ['Hotel', 'HotelGroup', 'HotelAssistance', 'HotelGroupAssistance'] },
    { key: 'TourPackage', label: 'Tour Package', icon: 'card_travel', types: ['TourPackage', 'TourPackageGroup', 'TourPackageAssistance', 'TourPackageGroupAssistance'] },
    { key: 'General', label: 'General', icon: 'help_outline', types: ['General'] },
];

/**
 * Maps each SERVICE_CATEGORIES key to the single MANAGE_CHAT_* permission
 * that controls access to that service type's chat channels.
 */
export const CATEGORY_PERMISSION_MAP: Record<string, PermissionCode> = {
    Flight: PermissionCode.MANAGE_CHAT_FLIGHT,
    Hotel: PermissionCode.MANAGE_CHAT_HOTEL,
    Visa: PermissionCode.MANAGE_CHAT_VISA,
    Passport: PermissionCode.MANAGE_CHAT_PASSPORT,
    Insurance: PermissionCode.MANAGE_CHAT_TRAVEL_INSURANCE,
    Permit: PermissionCode.MANAGE_CHAT_DRIVING_PERMIT,
    TourPackage: PermissionCode.MANAGE_CHAT_TOUR_PACKAGE,
    General: PermissionCode.MANAGE_CHAT_GENERAL,
};

/**
 * Returns only the ServiceCategory entries the user has permission for.
 * A category is allowed if the user's permissions include the matching
 * MANAGE_CHAT_* code, OR if the category has no entry in the map (safe
 * fallback for any future categories added without a permission gate).
 */
export function getAllowedCategories(
    permissions: PermissionCode[],
    allCategories: ServiceCategory[],
): ServiceCategory[] {
    return allCategories.filter((cat) => {
        const required = CATEGORY_PERMISSION_MAP[cat.key];
        return !required || permissions.includes(required);
    });
}

/**
 * Returns a flat array of all channel.type strings (e.g. 'Flight',
 * 'FlightGroup', 'FlightAssistance', ...) the user is permitted to access,
 * based on their MANAGE_CHAT_* permissions and the full SERVICE_CATEGORIES
 * definition.
 *
 * Used to inject a `serviceTypes` restriction into every backend query
 * (getAllChannels, getChannelStats) so that DynamoDB only returns channels
 * the agent is authorized to see.
 */
export function getAllowedChannelTypes(
    permissions: PermissionCode[],
    allCategories: ServiceCategory[],
): string[] {
    return getAllowedCategories(permissions, allCategories).flatMap((cat) => cat.types);
}
